/**
 * Compression codec — strategy pattern + registry (§11 v3)
 *
 * Built-in strategies:
 *   - 'deflate-raw': raw DEFLATE via fflate (default)
 *   - 'none':        passthrough (for pre-compressed content like .zip, .jpg)
 *
 * Extensible: register custom strategies via COMPRESSOR_REGISTRY.
 */

import { deflateSync, inflateSync } from 'fflate'

// ── Strategy interface ───────────────────────────────────────────

export interface CompressionStrategy {
    readonly id: string
    compress(data: Uint8Array): Uint8Array
    decompress(data: Uint8Array): Uint8Array
}

// ── Built-in strategies ──────────────────────────────────────────

const deflateRawStrategy: CompressionStrategy = {
    id: 'deflate-raw',
    compress(data: Uint8Array): Uint8Array {
        if (data.length === 0) return data
        return deflateSync(data, { level: 6 })
    },
    decompress(data: Uint8Array): Uint8Array {
        if (data.length === 0) return data
        return inflateSync(data)
    },
}

const noneStrategy: CompressionStrategy = {
    id: 'none',
    compress(data: Uint8Array): Uint8Array {
        return data
    },
    decompress(data: Uint8Array): Uint8Array {
        return data
    },
}

// ── Registry ─────────────────────────────────────────────────────

export const COMPRESSOR_REGISTRY = new Map<string, CompressionStrategy>([
    ['deflate-raw', deflateRawStrategy],
    ['none', noneStrategy],
])

export const DEFAULT_COMPRESSION = 'deflate-raw'

// ── MIME-based auto-skip ─────────────────────────────────────────

/**
 * MIME types that are already compressed — compression adds overhead, not savings.
 * These get 'none' compression automatically unless overridden.
 */
export const INCOMPRESSIBLE_MIMES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/heic',
    'image/heif',
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'audio/mpeg',
    'audio/mp4',
    'audio/ogg',
    'audio/aac',
    'application/zip',
    'application/gzip',
    'application/x-bzip2',
    'application/x-xz',
    'application/x-7z-compressed',
    'application/x-rar-compressed',
    'application/zstd',
    'application/pdf',  // internally compressed
])

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Resolve which compression strategy to use for a file.
 *
 * Priority: explicit override > MIME auto-skip > default.
 */
export function resolveCompression(opts?: { compression?: string; mime?: string }): CompressionStrategy {
    // Explicit override
    if (opts?.compression) {
        const strategy = COMPRESSOR_REGISTRY.get(opts.compression)
        if (!strategy) throw new Error(`Unsupported compression algorithm: '${opts.compression}'`)
        return strategy
    }

    // MIME auto-skip for pre-compressed content
    if (opts?.mime && INCOMPRESSIBLE_MIMES.has(opts.mime)) {
        return noneStrategy
    }

    // Default
    return deflateRawStrategy
}

/**
 * Get a decompression strategy by ID. Throws on unknown algorithm.
 */
export function getDecompressor(id: string): CompressionStrategy {
    const strategy = COMPRESSOR_REGISTRY.get(id)
    if (!strategy) throw new Error(`Unsupported compression algorithm: '${id}'`)
    return strategy
}
